/**
 * Pure calendar arithmetic on `YYYY-MM-DD` local dates (D520). Everything is done on UTC midnights, which have no
 * daylight-saving gaps, so a date never shifts; the only zone-aware helper is {@link localDateHourOf}, used to place an
 * instant on a market's wall clock.
 */
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** The calendar the BI answers for: a guard against absurd input, not a modelling limit. */
export const MIN_DATE = '2000-01-01';
export const MAX_DATE = '2099-12-31';
const DAY_MS = 86_400_000;
/** True when `value` is a real calendar date (`2025-02-29` is not) inside {@link MIN_DATE}..{@link MAX_DATE}. */
export function isRealDate(value) {
    if (typeof value !== 'string' || !ISO_DATE.test(value))
        return false;
    if (value < MIN_DATE || value > MAX_DATE)
        return false;
    const t = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}
/** Whole days since 1970-01-01 of a real date. */
export function toDayNumber(date) {
    return Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
}
export function fromDayNumber(n) {
    return new Date(n * DAY_MS).toISOString().slice(0, 10);
}
export function addDays(date, days) {
    return fromDayNumber(toDayNumber(date) + days);
}
/** Days from `a` to `b` (`b - a`). */
export function diffDays(a, b) {
    return toDayNumber(b) - toDayNumber(a);
}
/** Inclusive number of days of `from..to`. */
export function inclusiveDays(from, to) {
    return diffDays(from, to) + 1;
}
/**
 * The same month and day `years` earlier; 29 February clamps to 28 February. Used only to size the SOURCE envelope of a
 * year-over-year comparison (the exact day mapping is `dim_date.same_date_last_year`, which has no counterpart for a leap day).
 */
export function shiftYearsClamped(date, years) {
    const [y, m, d] = date.split('-').map(Number);
    const target = y + years;
    const probe = new Date(Date.UTC(target, m - 1, d));
    if (probe.getUTCMonth() !== m - 1)
        return new Date(Date.UTC(target, m - 1, d - 1)).toISOString().slice(0, 10);
    return probe.toISOString().slice(0, 10);
}
export function firstOfMonth(date) {
    return `${date.slice(0, 7)}-01`;
}
export function lastOfMonth(date) {
    const [y, m] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}
export function firstOfYear(date) {
    return `${date.slice(0, 4)}-01-01`;
}
export function lastOfYear(date) {
    return `${date.slice(0, 4)}-12-31`;
}
/** The month before the one `date` is in: its first and last day. */
export function previousMonth(date) {
    const lastOfPrev = addDays(firstOfMonth(date), -1);
    return { from: firstOfMonth(lastOfPrev), to: lastOfPrev };
}
/** The first day of the calendar quarter `date` is in. */
export function firstOfQuarter(date) {
    const month = Number(date.slice(5, 7));
    const first = Math.floor((month - 1) / 3) * 3 + 1;
    return `${date.slice(0, 4)}-${String(first).padStart(2, '0')}-01`;
}
/** The quarter before the one `date` is in: its first and last day. */
export function previousQuarter(date) {
    const lastOfPrev = addDays(firstOfQuarter(date), -1);
    return { from: firstOfQuarter(lastOfPrev), to: lastOfPrev };
}
/** The Monday of the ISO week `date` is in. */
export function mondayOf(date) {
    return addDays(date, -(isoWeekday(date) - 1));
}
/** ISO weekday of a date: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date) {
    const d = new Date(`${date}T00:00:00Z`).getUTCDay();
    return d === 0 ? 7 : d;
}
/** `YYYY-Qn` of a date. */
export function quarterOf(date) {
    const month = Number(date.slice(5, 7));
    return `${date.slice(0, 4)}-Q${Math.floor((month - 1) / 3) + 1}`;
}
/** The wall clock of `instant` (a UTC ISO instant) in the IANA zone `timeZone`; undefined for a bad instant or zone. */
export function localDateHourOf(instant, timeZone) {
    const ms = Date.parse(instant);
    if (!Number.isFinite(ms))
        return undefined;
    try {
        const format = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
        });
        const wall = (at) => {
            const parts = format.formatToParts(new Date(at));
            const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
            const hour = Number(get('hour'));
            const date = `${get('year')}-${get('month')}-${get('day')}`;
            return isRealDate(date) && Number.isInteger(hour) ? { date, hour: hour === 24 ? 0 : hour } : undefined;
        };
        const now = wall(ms);
        if (now === undefined)
            return undefined;
        // The same wall-clock hour one real hour earlier means this instant is the second pass of a repeated hour.
        const earlier = wall(ms - 3_600_000);
        const occurrence = earlier !== undefined && earlier.date === now.date && earlier.hour === now.hour ? 2 : 1;
        return { ...now, occurrence };
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=dates.js.map